import { UIElement } from "../../common_ui_elements/ui_element.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { DEFAULT_LEADERBOARD_SCREEN_PLAYER_INFO_CONFIG, LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";
import { UIAvatarSelectionItem } from "../../common_ui_elements/ui_avatar_selection_item.js";
import { TelegramAvatar } from "../../../common/telegram_avatar.js";

const TOP_RANK_LIMIT = 3;

const RANK_BOX_W = 166;
const RANK_TEXT_SIZE = 55;

const AVATAR_X = 176;
const AVATAR_SIZE = 127;

const NAME_X = 316;
const NAME_W = 360;
const NAME_TEXT_SIZE = 50;

const LEVEL_BLOCK_X = 700;
const LEVEL_BLOCK_W = 320;
const LEVEL_LABEL_TEXT_SIZE = 25;
const LEVEL_VALUE_TEXT_SIZE = 55;

export class LeaderboardScreenPlayerInfo extends UIElement
{
  private readonly config: LeaderboardScreenPlayerInfoConfig;
  private render_clip: { x: number; y: number; w: number; h: number } | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    config: LeaderboardScreenPlayerInfoConfig = DEFAULT_LEADERBOARD_SCREEN_PLAYER_INFO_CONFIG
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.config = config;
    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createPlayerRating();
    this.createPlayerAvatar();
    this.createPlayerName();
    this.createPlayerLevel();
  }

  private backgroundImageId(): ImgID
  {
    if ( this.config.is_player )
      return ImgID.UI_LeaderboardItemBgYou;

    if ( this.config.rating <= TOP_RANK_LIMIT )
      return ImgID.UI_LeaderboardItemBgTop;

    return ImgID.UI_LeaderboardItemBg;
  }

  private createBackground(): void
  {
    const background = new UIImage(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      this.backgroundImageId()
    );

    background.setClickable( false );
    this.addChild( background );
  }

  private createPlayerRating(): void
  {
    const rating_text = new UITextElement(
      this.ctx,
      0,
      0,
      LayoutScaler.widthFromDesign( RANK_BOX_W ),
      this.rect.getH(),
      {
        text: `${this.config.rating}`,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( RANK_TEXT_SIZE ),
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.leaderboard_card_outline,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );

    rating_text.setClickable( false );
    this.addChild( rating_text );
  }

  private createPlayerAvatar(): void
  {
    const avatar_size = LayoutScaler.widthFromDesign( AVATAR_SIZE );

    const avatar = new UIAvatarSelectionItem(
      this.ctx,
      LayoutScaler.widthFromDesign( AVATAR_X ),
      ( this.rect.getH() - avatar_size ) / 2,
      avatar_size,
      avatar_size,
      this.config.avatar_id,
      () => {},
      LayoutScaler.widthFromDesign( 4 ),
      0,
      undefined,
      true,
      {
        type: "linear-gradient",
        from: "#F9F4C9",
        to: "#B5A084",
        direction: "vertical"
      },
      this.config.facebook_av_url,
      this.config.is_player
        ? TelegramAvatar.currentIdentity()
        : TelegramAvatar.identityFromName( this.config.name )
    );

    avatar.setClickable( false );
    this.addChild( avatar );
  }

  private createPlayerName(): void
  {
    const name_h = LayoutScaler.heightFromDesign( 60 );

    const player_name = new UITextElement(
      this.ctx,
      LayoutScaler.widthFromDesign( NAME_X ),
      ( this.rect.getH() - name_h ) / 2,
      LayoutScaler.widthFromDesign( NAME_W ),
      name_h,
      {
        text: `${this.config.name}`,
        text_color: ColorScheme.leaderboard_card_name,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( NAME_TEXT_SIZE ),
        font_weight: 900,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );

    player_name.setClickable( false );
    this.addChild( player_name );
  }

  private createPlayerLevel(): void
  {
    const block_x = LayoutScaler.widthFromDesign( LEVEL_BLOCK_X );
    const block_w = LayoutScaler.widthFromDesign( LEVEL_BLOCK_W );

    const label = new UITextElement(
      this.ctx,
      block_x,
      LayoutScaler.heightFromDesign( 20 ),
      block_w,
      LayoutScaler.heightFromDesign( 36 ),
      {
        localization_key: "LEVEL",
        text_color: ColorScheme.leaderboard_card_outline,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( LEVEL_LABEL_TEXT_SIZE ),
        font_weight: 900,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );

    label.setClickable( false );
    this.addChild( label );

    const value = new UITextElement(
      this.ctx,
      block_x,
      LayoutScaler.heightFromDesign( 52 ),
      block_w,
      LayoutScaler.heightFromDesign( 66 ),
      {
        text: this.config.level.toString().replace( /\B(?=(\d{3})+(?!\d))/g, ' ' ),
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( LEVEL_VALUE_TEXT_SIZE ),
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.leaderboard_card_outline,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );

    value.setClickable( false );
    this.addChild( value );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.render_clip )
    {
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect( this.render_clip.x, this.render_clip.y, this.render_clip.w, this.render_clip.h );
      this.ctx.clip();
    }

    this.startRender();
    this.finishRender();

    if ( this.render_clip )
      this.ctx.restore();
  }

  public setRenderClip( x: number, y: number, w: number, h: number ): void
  {
    this.render_clip = { x, y, w, h };
  }

  public clearRenderClip(): void
  {
    this.render_clip = null;
  }

  public name(): string
  {
    return "LeaderboardScreenPlayerInfo";
  }
}
